import React, {Component} from 'react';
import PropTypes from 'prop-types';
import IntersectionObserverManager from '../lazy-loader/IntersectionObserverManager';

// An element invisible to the user but not to the browser
export const INVISIBLE_ELEMENT = <div style={{height: 1, marginTop: -1}} />;

const observe = (element, callback, options) => {
    const observer = IntersectionObserverManager.getInstance(callback, options, element);
    observer.observe(element);
    return () => observer.unobserve(element);
};

/**
 * The IntersectionObserver API wrapped as a React component
 */
export default class ReactIntersectionObserver extends Component {
    static propTypes = {
        /** The element to observe. If a non-DOM element is provided, make sure it forwards refs (see https://reactjs.org/docs/forwarding-refs.html) */
        children: PropTypes.node,
        /** See https://developer.mozilla.org/en-US/docs/Web/API/IntersectionObserver/rootMargin */
        rootMargin: PropTypes.string,
        /** Called on intersection changes with the relevant IntersectionObserverEntry */
        onIntersection: PropTypes.func.isRequired
    }

    constructor(props) {
        super(props);
        this.target = React.createRef();
    }

    componentDidMount() {
        const {onIntersection, rootMargin} = this.props;
        this.unobserve = observe(this.target.current, onIntersection, {rootMargin});
    }

    componentWillUnmount() {
        this.unobserve();
    }

    render() {
        const {children} = this.props;
        const child = children || INVISIBLE_ELEMENT;
        return React.cloneElement(child, {ref: this.target});
    }
}
